import classNames from 'classnames';

type ToastElement = HTMLLIElement & {
    timeoutId?: number;
    closeCallback?: () => void;
};

type ToastType = 'success' | 'info' | 'warning' | 'error' | 'default';

const iconPaths: Record<ToastType, string> = {
    default: '',
    success: '/images/icons/toast-icon-success.svg',
    info: '/images/icons/toast-icon-info.svg',
    warning: '/images/icons/toast-icon-warning.svg',
    error: '/images/icons/toast-icon-error.svg',
};

const getCloseIconPath = (type: ToastType) => {
    return type === 'default'
        ? '/images/icons/close-toast-white.svg'
        : '/images/icons/close-toast-black.svg';
};

/**
 * Toasts - a class for creating and managing toast notifications.
 *
 * Toasts are small notifications that appear at the top of the screen.
 *
 * They are used to provide feedback to the user, such as when an action has been completed successfully, or when an error has occurred.
 *
 * @param {HTMLULElement} container - The container to which the toast message should be added. This should be a `<ul>` element.
 */

type AddToastParams = {
    type: ToastType;
    message: string;
    autoClose?: boolean;
    closeCallback?: () => void;
};

class Toasts {
    notifications: HTMLDivElement | null;

    constructor() {}

    initContainer() {
        const container = document.createElement('div');
        container.setAttribute('id', 'toast_container');
        container.className = classNames('grid justify-center');
        container.dataset.toasts = '';
        document.body.appendChild(container);
        this.notifications = container;
        return () => container.remove();
    }

    removeToast(toast: ToastElement) {
        toast.setAttribute('data-toast-hide', 'true');
        if (toast.timeoutId) clearTimeout(toast.timeoutId);
        return setTimeout(() => {
            toast.remove();
            toast.closeCallback();
        }, 600);
    }

    /**
     * Adds a toast notification to the UI.
     *
     * @param type - The ID of the toast ('success', 'info', 'warning', 'error', or 'default').
     * @param message - The message of the toast.
     * @param [autoClose=true] - An optional param that indicates whether the toast should automatically close after a certain duration. Default is true.
     * @param [closeCallback] - An optional callback function that will be called when the toast is closed.
     */
    addToast({
        type,
        message,
        autoClose = true,
        closeCallback,
    }: AddToastParams) {
        const removeContainer = this.initContainer();
        const toast = document.createElement('li') as ToastElement;
        toast.closeCallback = () => {
            closeCallback?.();
            removeContainer();
        };
        toast.className = classNames(
            `${type}`,
            'data-toast',
            'flex justify-between gap-5 items-center w-fit min-w-[335px]',
            'overflow-hidden bg-white',
            'animate-[toast-in_400ms_cubic-bezier(0.18,0.89,0.32,1.28)_forwards] data-[toast-hide=true]:animate-[toast-out_400ms_cubic-bezier(0.48,-0.26,0.87,0.03)_forwards]',
            ' rounded-lg p-4',
            autoClose
                ? ' before:absolute before:h-[var(--toast-progress-bar-height)] before:w-full  before:bottom-0 before:left-0 before:animate-[progress_5s_linear_forwards]'
                : '',
            'shadow-xl',
        );

        const closeButton = document.createElement('button');
        closeButton.classList.add('shrink-0', 'cursor-pointer');
        closeButton.dataset.toastClose = '';
        closeButton.innerHTML = `<img  src=${getCloseIconPath(type)} width="25" height="25" alt="toast close button">`;

        closeButton.addEventListener('click', removeContainer);

        toast.innerHTML = `<div class="flex items-center gap-4">
                              ${
                                  iconPaths[type]
                                      ? `<img
                                src="${iconPaths[type]}"
                                alt="toast ${type} icon"
                              />`
                                      : ''
                              }
                              <div class="grid mr-auto">
                                <span class="font-normal leading-tight">${message}</span>
                              </div>
                           </div>`;

        toast.append(closeButton);
        this.notifications?.appendChild(toast);

        if (autoClose) {
            toast.timeoutId = setTimeout(() => {
                this.removeToast(toast);
            }, 5000) as unknown as number;
        }
    }
}

export const toast = new Toasts();
